/**
 * 2D chain layout — turns the engine's linear placement order into world
 * positions on the table, snaking horizontally with 90° L-bends when a row
 * runs past `maxRowWidth`.
 *
 * Visual model:
 *   Row 0 runs LEFT → RIGHT along +x.
 *   When the next tile would overflow `maxRowWidth`, it's placed as a
 *     CORNER tile (perpendicular orientation) in the gap between rows.
 *   Row 1 runs RIGHT → LEFT.
 *   Row 2 runs LEFT → RIGHT again. Etc.
 *
 * Tiles in a row are oriented along x; corner tiles and in-row doubles
 * stand perpendicular along z. Tile rotation also flips when the row
 * direction reverses so the matched-pip side stays adjacent to the chain.
 *
 * Coordinates are then re-centered so the whole chain straddles origin —
 * caller draws hand rows at fixed ±z and lets the chain grow into the
 * middle.
 */

import type { Placement } from '../../../engine/dominoes/chain.js';

export interface ChainSlot {
  x: number;
  z: number;
  rotY: number;
}

export interface ChainBBox {
  width: number;
  depth: number;
}

export interface LayoutOptions {
  /** Long axis (parallel-to-chain) tile dimension. */
  longSide: number;
  /** Short axis (perpendicular-to-chain) tile dimension. */
  shortSide: number;
  /** Visible gap between adjacent placements in a row. */
  gap: number;
  /** Z-axis spacing between successive row chain centerlines. */
  rowGap: number;
  /** A new row starts whenever the next placement would push past this width. */
  maxRowWidth: number;
}

function tileRotForRow(p: Placement, dirInRow: 1 | -1): number {
  if (p.double) return Math.PI / 2;
  // Default tile mesh: low at -x, high at +x.
  const isLowOnLeft = p.leftPip === p.domino.low;
  if (dirInRow === 1) {
    // Row going +x: matched pip (leftPip) lands on -x edge of placement.
    return isLowOnLeft ? 0 : Math.PI;
  }
  // Row going -x: matched pip lands on +x edge of placement.
  return isLowOnLeft ? Math.PI : 0;
}

function cornerRot(p: Placement): number {
  if (p.double) return Math.PI / 2;
  // Corner tile is perpendicular: long axis along z. Its matched pip side
  // (leftPip) faces -z (the previous row, where chain order came from).
  // rotY = -π/2 → original -x ends up at -z (low on top): use when leftPip == low.
  // rotY = +π/2 → original +x ends up at -z (high on top): use when leftPip == high.
  const isLowOnLeft = p.leftPip === p.domino.low;
  return isLowOnLeft ? -Math.PI / 2 : Math.PI / 2;
}

export function layoutChain2D(
  placements: readonly Placement[],
  opts: LayoutOptions,
): { slots: ChainSlot[]; bbox: ChainBBox } {
  const N = placements.length;
  if (N === 0) return { slots: [], bbox: { width: 0, depth: 0 } };

  const { longSide, shortSide, gap, rowGap, maxRowWidth } = opts;

  const slots: ChainSlot[] = new Array(N);
  let row = 0;
  let dir: 1 | -1 = 1;
  let rowStartX = 0;
  let xInRow = 0;

  for (let i = 0; i < N; i++) {
    const p = placements[i]!;
    const slotLen = p.double ? shortSide : longSide;

    // Overflow → place this tile perpendicular as a row-ending corner.
    if (xInRow + slotLen > maxRowWidth && i > 0) {
      const cornerLen = shortSide;       // along row direction
      const cornerCenterX = rowStartX + dir * (xInRow + cornerLen / 2);
      const cornerCenterZ = (row + 0.5) * rowGap;
      slots[i] = { x: cornerCenterX, z: cornerCenterZ, rotY: cornerRot(p) };
      // Hand off to the next row, starting under the corner.
      row += 1;
      dir = (dir === 1 ? -1 : 1);
      rowStartX = cornerCenterX;
      xInRow = cornerLen / 2 + gap;
      continue;
    }

    const x = rowStartX + dir * (xInRow + slotLen / 2);
    const z = row * rowGap;
    slots[i] = { x, z, rotY: tileRotForRow(p, dir) };
    xInRow += slotLen + gap;
  }

  // Bounding box (use longSide/2 as conservative half-extent in both axes,
  // so corners and in-row doubles are accommodated).
  const r = longSide / 2;
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const s of slots) {
    minX = Math.min(minX, s.x - r);
    maxX = Math.max(maxX, s.x + r);
    minZ = Math.min(minZ, s.z - r);
    maxZ = Math.max(maxZ, s.z + r);
  }
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;
  for (const s of slots) {
    s.x -= cx;
    s.z -= cz;
  }
  return { slots, bbox: { width: maxX - minX, depth: maxZ - minZ } };
}
